import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Button,
    Typography,
    SvgIcon,
    Card,
    CircularProgress,
    Stack,
} from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import axios from 'axios';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { API_PATH } from '../../../App';
import { Avatar } from '@mui/material';

interface AccountBalance {
    currency: string;
    balance: string;
}

interface NicehashBalance {
    currency: string;
    totalBalance: number;
    available: number;
    minBalanceRequired: number;
    isLowBalance: boolean;
}

interface CombinedBalance {
    currency: string;
    balance: number;
    vendor: 'Gate.io' | 'Nicehash';
}

const AccountBalanceDisplay = () => {
    const [gateioBalances, setGateioBalances] = useState<CombinedBalance[]>([]);
    const [nicehashBalances, setNicehashBalances] = useState<CombinedBalance[]>([]);
    const [loadingGateio, setLoadingGateio] = useState(true);
    const [loadingNicehash, setLoadingNicehash] = useState(true);

    useEffect(() => {
        fetchGateio();
        fetchNicehash();
    }, []);

    const fetchGateio = async () => {
        setLoadingGateio(true);
        try {
            const res = await axios.get(API_PATH + '/accounts/gateio', getAxiosConfig());
            const data: CombinedBalance[] = Array.isArray(res.data)
                ? res.data.map((item: AccountBalance) => ({
                    currency: item.currency,
                    balance: parseFloat(item.balance),
                    vendor: 'Gate.io',
                }))
                : [];
            setGateioBalances(data);
        } catch (error) {
            console.error('Error fetching Gate.io balances:', error);
        } finally {
            setLoadingGateio(false);
        }
    };

    const fetchNicehash = async () => {
        setLoadingNicehash(true);
        try {
            const res = await axios.get(API_PATH + '/healthCheckApi/niceHashWalletInfo', getAxiosConfig());
            const data: CombinedBalance[] =
                res.data && typeof res.data === 'object'
                    ? [
                        {
                            currency: res.data.currency,
                            balance: res.data.available,
                            vendor: 'Nicehash',
                        },
                    ]
                    : [];
            setNicehashBalances(data);
        } catch (error) {
            console.error('Error fetching Nicehash balances:', error);
        } finally {
            setLoadingNicehash(false);
        }
    };

    

    // Optional: color map for different currencies
    const currencyColors: Record<string, string> = {
        BTC: '#f7931a',
        ETH: '#3c3c3d',
        ALPH: '#ff3e3e',
        KAS:'#6deed4',
        USDT: '#26a17b',
        LTC: '#b8b8b8',
        XRP: '#25aae1',
        DOGE: '#c2a633',
        DEFAULT: '#1977d3',
        
    };

    const getCurrencyColor = (currency: string) =>
        currencyColors[currency.toUpperCase()] || currencyColors.DEFAULT;

    const renderBalanceGroup = (
        label: string,
        balances: CombinedBalance[],
        loading: boolean,
        onRefresh: () => void
    ) => (
        <Box sx={{ mb: 5 }}>
            <Stack direction="row" alignItems="center" justifyContent="start" gap={2} sx={{ mb: 2, mx: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#222' }}>
                    {label}
                </Typography>
                <Button
                    onClick={onRefresh}
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshRounded />}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: '#ccc',
                        color: '#555',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#bbb',
                        },
                    }}
                >
                    Refresh
                </Button>
            </Stack>

            {loading ? (
                <Box sx={{ pl: 2 }}>
                    <CircularProgress size={24} sx={{ color: '#1977d3' }} />
                </Box>
            ) : balances.length > 0 ? (
                <Grid container spacing={2}>
                    {balances.map((item, idx) => {
                        const bgColor = getCurrencyColor(item.currency);
                        return (
                            <Grid item xs={12} sm={6} md={3} key={`${item.vendor}-${idx}`}>
                                <Card
                                    elevation={2}
                                    sx={{
                                        p: 2,
                                        borderRadius: '16px',
                                        backgroundColor: '#fafafa',
                                        border: '1px solid #e0e0e0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                        transition: 'transform 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        },
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: bgColor, fontWeight: 600 }}>
                                        {item.currency[0]}
                                    </Avatar>
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 600, color: '#333' }}
                                        >
                                            {item.currency}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500, color: '#388e3c' }}
                                        >
                                            {item.balance.toFixed(8)}
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2 }}>
                    No balances available.
                </Typography>
            )}
        </Box>
    );



    return (
        <Box sx={{ width: '100%', padding: 2 }}>
            {renderBalanceGroup('Gate.io Accounts', gateioBalances, loadingGateio, fetchGateio)}
            {renderBalanceGroup('Nicehash Accounts', nicehashBalances, loadingNicehash, fetchNicehash)}
        </Box>
    );
};

export default AccountBalanceDisplay;
